import React from 'react';
import AuthAdminUserModel from '../../code/models/AuthAdminUserModel';
import CclDataTable, { CclDataTableColumnDef, CclDataTableColumnType } from '../CclDataTable';
import ApiBidPeriod from '../../code/api/ApiBidPeriod';
import BidPeriod from '../../code/models/BidPeriod';
import '../../css/adminbidperiodsform.css';
import Toaster from "../Toaster";

interface IAdminBidPeriodsForm {
    adminUser: AuthAdminUserModel;
}

export default function AdminBidPeriodsForm({ adminUser }: IAdminBidPeriodsForm) {
    const [toastMessage, setToastMessage] = React.useState<string>("");
    const [bidPeriods, setBidPeriods] = React.useState<BidPeriod[] | null>(null);
    const [selectedBidPeriod, setSelectedBidPeriod] = React.useState<BidPeriod | null>(null);
    const [editBidPeriod, setEditBidPeriod] = React.useState<BidPeriod | null>(null);
    const [anyChanges, setAnyChanges] = React.useState<boolean>(false);

    React.useEffect(() => {
        loadBidPeriods();
    }, []);

    async function loadBidPeriods() {
        ApiBidPeriod.getBidPeriods().then(data => {
            setBidPeriods(data);
        });
    }

    function reloadBidPeriods() {
        setEditBidPeriod(null);
        setSelectedBidPeriod(null);
        setBidPeriods(null);
        ApiBidPeriod.getBidPeriods().then(data => {
            setBidPeriods(data);
        });
    }

    function saveChanges() {
        setToastMessage("");
        if (anyChanges && !!editBidPeriod && !!selectedBidPeriod) {
            ApiBidPeriod.updateBidPeriod(editBidPeriod).then(success => {
                if (success) {
                    selectedBidPeriod.month = editBidPeriod.month;
                    selectedBidPeriod.monthStart = editBidPeriod.monthStart;
                    selectedBidPeriod.monthEnd = editBidPeriod.monthEnd;
                    selectedBidPeriod.bidStart = editBidPeriod.bidStart;
                    selectedBidPeriod.bidEnd = editBidPeriod.bidEnd;

                    handleRowSelect(selectedBidPeriod);
                    setToastMessage("Bid Period Saved.");
                }
            });
        }
    }

    function checkEditBidPeriodHasChanges() {
        var isAnyChanges = editBidPeriod !== null &&
            (selectedBidPeriod?.month !== editBidPeriod?.month ||
                selectedBidPeriod?.monthStart !== editBidPeriod?.monthStart ||
                selectedBidPeriod?.monthEnd !== editBidPeriod?.monthEnd ||
                selectedBidPeriod?.bidStart !== editBidPeriod?.bidStart ||
                selectedBidPeriod?.bidEnd !== editBidPeriod?.bidEnd);
        setAnyChanges(isAnyChanges);
    }

    function getButtonStateClass(isActive: Boolean, additionalClasses: string) {
        const classnames = ["btn"];
        const mainStyle = isActive ? "btn-primary" : "btn-outline-primary";
        classnames.push(mainStyle);

        if (!!additionalClasses) {
            classnames.push(additionalClasses);
        }

        return classnames.join(" ");
    }

    function handleRowSelect(rowData: any) {
        setSelectedBidPeriod(rowData);
        setEditBidPeriod(structuredClone(rowData));
        setAnyChanges(false);
    }

    const profileColumnDefs: CclDataTableColumnDef[] = [
        {
            fieldName: "month",
            title: "Bid Month",
            visible: true,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "monthStart",
            title: "Month Start",
            visible: true,
            formatData: formatDateOnly,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "monthEnd",
            title: "Month End",
            visible: true,
            formatData: formatDateOnly,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "bidStart",
            title: "Bid Start",
            visible: true,
            formatData: formatDate,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "bidEnd",
            title: "Bid End",
            visible: true,
            formatData: formatDate,
            type: CclDataTableColumnType.Data
        }
    ];

    function formatDateOnly(val: string) {
        let sval = val.replace("T", " ");
        let dval = new Date(sval);
        return Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).format(dval);
    }

    function formatDate(val: string) {
        let sval = val.replace("T", " ");
        let dval = new Date(sval);
        sval = Intl.DateTimeFormat('en-us', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }).format(dval);
        return sval.replace(/, /g, " ");
    }

    function editDateOnly(val: string) {
        return val.split("T")[0];
    }

    function editDateTime(val: string) {
        return val.replace("T", " ");
    }

    function buildBidPeriodEditor() {
        return (<React.Fragment>
            {!!editBidPeriod && !!selectedBidPeriod &&
                <div key={"bp" + selectedBidPeriod.bidPeriodId} className='bidperiod-form'>
                    <div>
                        <label htmlFor="editBidPeriodMonth" className="form-label">Bid Month</label>
                        <input className="form-control" id="editBidPeriodMonth" defaultValue={selectedBidPeriod.month}
                            maxLength={9} autoComplete={"off"}
                            onChange={(e) => {
                                editBidPeriod.month = e.target.value;
                                checkEditBidPeriodHasChanges();
                            }}></input>
                    </div>
                    <div>
                        <label htmlFor="editBidPeriodMonthStart" className="form-label">Month Start</label>
                        <input type="date" className="form-control" id="editBidPeriodMonth"
                            defaultValue={`${editDateOnly(selectedBidPeriod.monthStart)}`}
                            autoComplete={"off"} onChange={(e) => {
                                editBidPeriod.monthStart = e.target.value;
                                checkEditBidPeriodHasChanges();
                            }}></input>
                    </div>
                    <div>
                        <label htmlFor="editBidPeriodMonthEnd" className="form-label">Month End</label>
                        <input type="date" className="form-control" id="editBidPeriodMonth"
                            defaultValue={`${editDateOnly(selectedBidPeriod.monthEnd)}`}
                            autoComplete={"off"} onChange={(e) => {
                                editBidPeriod.monthEnd = e.target.value;
                                checkEditBidPeriodHasChanges();
                            }}></input>
                    </div>
                    <div className="start-end">
                        <label htmlFor="editBidPeriodBiddingStart" className="form-label">Bidding Start</label>
                        <input type="datetime-local" className="form-control" id="editBidPeriodMonth"
                            defaultValue={editDateTime(selectedBidPeriod.bidStart)}
                            autoComplete={"off"} onChange={(e) => {
                                editBidPeriod.bidStart = e.target.value;
                                checkEditBidPeriodHasChanges();
                            }}></input>
                    </div>
                    <div className="start-end">
                        <label htmlFor="editBidPeriodBiddingEnd" className="form-label">Bidding End</label>
                        <input type="datetime-local" className="form-control" id="editBidPeriodMonth"
                            defaultValue={editDateTime(selectedBidPeriod.bidEnd)}
                            autoComplete={"off"} onChange={(e) => {
                                editBidPeriod.bidEnd = e.target.value;
                                checkEditBidPeriodHasChanges();
                            }}></input>
                    </div>
                    <div>
                        <button disabled={!anyChanges} className="btn btn-outline-primary bp-save-button"
                            onClick={saveChanges}>Save Changes</button>
                    </div>
                </div>}
        </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <div className='buttonbar'>
                <button disabled={!bidPeriods} className={getButtonStateClass(true, "")} onClick={reloadBidPeriods}>Reload</button>
            </div>
            {buildBidPeriodEditor()}
            <div className='bidperiod-table-wrapper'>
                <CclDataTable
                    name={"profilesTable"}
                    tableData={bidPeriods || []}
                    columnDefinitions={profileColumnDefs}
                    rowSelect={true}
                    onRowSelect={handleRowSelect}
                    onButtonClick={null} />
            </div>
            <Toaster title="Bid Periods" subtitle="" message={toastMessage} key={"bidperiodtoast"} />
        </React.Fragment>

    )
}
